import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import type { IncidentPosition } from 'hooks/incident/useRoster';

import type {
  DraftRosterAssignment,
  DraftRosterSeat,
  DraftSeatDesignation,
  PersonPick,
  RosterNodePayload,
} from './createIncidentModel';

export type TemplateNode = {
  name: string;
  parentName: string | null;
  isWorkgroup?: boolean;
  /**
   * Only org-saved templates carry a category; built-in catalog nodes leave it
   * unset.
   */
  category?: string;
  customCategory?: string | null;
};

/** Same tree the backend seeds when createIncident gets no slug or nodes. */
export const DEFAULT_ICS_ROSTER_NODES: TemplateNode[] = [
  {
    name: 'Incident Commander',
    parentName: null,
    isWorkgroup: false,
  },
  {
    name: 'Public Information Officer',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Legal Officer',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Safety Officer',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Liaison Officer',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Operations Section Chief',
    parentName: 'Incident Commander',
    isWorkgroup: true,
  },
  {
    name: 'Staging Area Manager',
    parentName: 'Operations Section Chief',
    isWorkgroup: true,
  },
  {
    name: 'Planning Section Chief',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Resource Unit Leader',
    parentName: 'Planning Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Situation Unit Leader',
    parentName: 'Planning Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Documentation Unit Leader',
    parentName: 'Planning Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Demobilization Unit Leader',
    parentName: 'Planning Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Environmental Unit Leader',
    parentName: 'Planning Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Technical Specialist',
    parentName: 'Planning Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Logistics Section Chief',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Service Branch Director',
    parentName: 'Logistics Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Communications Unit Leader',
    parentName: 'Service Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Medical Unit Leader',
    parentName: 'Service Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Food Unit Leader',
    parentName: 'Service Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Support Branch Director',
    parentName: 'Logistics Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Supply Unit Leader',
    parentName: 'Support Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Facilities Unit Leader',
    parentName: 'Support Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Ground Support Unit Leader',
    parentName: 'Support Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Vessel Support Unit Leader',
    parentName: 'Support Branch Director',
    isWorkgroup: false,
  },
  {
    name: 'Finance Section Chief',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Time Unit Leader',
    parentName: 'Finance Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Procurement Unit Leader',
    parentName: 'Finance Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Compensation Unit Leader',
    parentName: 'Finance Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Cost Unit Leader',
    parentName: 'Finance Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Intel/Investigations Section Chief',
    parentName: 'Incident Commander',
    isWorkgroup: false,
  },
  {
    name: 'Investigative Operations Group Supervisor',
    parentName: 'Intel/Investigations Section Chief',
    isWorkgroup: false,
  },
  {
    name: 'Intelligence Group Supervisor',
    parentName: 'Intel/Investigations Section Chief',
    isWorkgroup: false,
  },
  { name: 'Qualified Individual', parentName: null, isWorkgroup: false },
];

const INCIDENT_COMMANDER_NAMES = new Set([
  'Incident Commander',
  'TEMA Director',
]);

export function isIncidentCommanderSeatName(name: string): boolean {
  return INCIDENT_COMMANDER_NAMES.has(name);
}

function nextSeatId(name: string, used: Set<string>): string {
  let id = `draft:${name}`;
  let suffix = 2;
  while (used.has(id)) {
    id = `draft:${name}:${suffix++}`;
  }
  used.add(id);
  return id;
}

/** Materialize catalog / default ICS nodes into client seats with stable ids. */
export function seatsFromTemplateNodes(
  nodes: TemplateNode[]
): DraftRosterSeat[] {
  const used = new Set<string>();
  const idByName = new Map<string, string>();
  for (const node of nodes) {
    if (!idByName.has(node.name)) {
      idByName.set(node.name, nextSeatId(node.name, used));
    }
  }
  return nodes.map((node) => ({
    id: idByName.get(node.name) as string,
    name: node.name,
    parentId: node.parentName ? (idByName.get(node.parentName) ?? null) : null,
    isWorkgroup: node.isWorkgroup,
    category: node.category,
    customCategory: node.customCategory,
  }));
}

/**
 * Attach an expansion (or any named tree) under an existing parent. Roots
 * (`parentName` null) hang off the selected seat. Nodes whose title is already
 * on the chart are skipped, matching live attach-expansion.
 */
export function attachNodesUnderParent(
  seats: DraftRosterSeat[],
  parentId: string,
  nodes: TemplateNode[]
): DraftRosterSeat[] {
  const existingNames = new Set(seats.map((seat) => seat.name));
  const used = new Set(seats.map((seat) => seat.id));
  const idByName = new Map(seats.map((seat) => [seat.name, seat.id]));
  const next = [...seats];

  for (const node of nodes) {
    if (existingNames.has(node.name)) continue;
    const id = nextSeatId(node.name, used);
    const parentName = node.parentName;
    const resolvedParentId = parentName
      ? (idByName.get(parentName) ?? parentId)
      : parentId;
    idByName.set(node.name, id);
    existingNames.add(node.name);
    next.push({
      id,
      name: node.name,
      parentId: resolvedParentId,
      isWorkgroup: node.isWorkgroup,
      category: node.category,
      customCategory: node.customCategory,
    });
  }
  return next;
}

export function addDraftPosition(
  seats: DraftRosterSeat[],
  parentId: string,
  name: string,
  isWorkgroup?: boolean
): DraftRosterSeat[] {
  const title = name.trim();
  if (!title || !seats.some((seat) => seat.id === parentId)) return seats;
  const used = new Set(seats.map((seat) => seat.id));
  return [
    ...seats,
    {
      id: nextSeatId(title, used),
      name: title,
      parentId,
      isWorkgroup,
    },
  ];
}

/**
 * Add a top-level seat. Used when the chart is empty, so there is no parent to
 * hang a new position under.
 */
export function addDraftRootSeat(
  seats: DraftRosterSeat[],
  name: string,
  isWorkgroup?: boolean
): DraftRosterSeat[] {
  const title = name.trim();
  if (!title) return seats;
  const used = new Set(seats.map((seat) => seat.id));
  return [
    ...seats,
    {
      id: nextSeatId(title, used),
      name: title,
      parentId: null,
      isWorkgroup,
    },
  ];
}

/**
 * Set the ICS element a seat represents. Leaving Custom drops the typed label,
 * matching how the live roster stores category + custom_category.
 */
export function setDraftSeatCategory(
  seats: DraftRosterSeat[],
  seatId: string,
  category: string,
  customCategory?: string | null
): DraftRosterSeat[] {
  return seats.map((seat) =>
    seat.id === seatId
      ? {
          ...seat,
          category,
          customCategory:
            category === 'Custom' ? (customCategory ?? null) : null,
        }
      : seat
  );
}

export function reparentDraftSeat(
  seats: DraftRosterSeat[],
  seatId: string,
  parentId: string | null
): DraftRosterSeat[] {
  if (seatId === parentId) return seats;
  if (parentId && wouldCreateCycle(seats, seatId, parentId)) return seats;
  return seats.map((seat) =>
    seat.id === seatId ? { ...seat, parentId } : seat
  );
}

function wouldCreateCycle(
  seats: DraftRosterSeat[],
  seatId: string,
  parentId: string
): boolean {
  const parentById = new Map(seats.map((seat) => [seat.id, seat.parentId]));
  let cursor: string | null = parentId;
  const seen = new Set<string>();
  while (cursor) {
    if (cursor === seatId) return true;
    if (seen.has(cursor)) return true;
    seen.add(cursor);
    cursor = parentById.get(cursor) ?? null;
  }
  return false;
}

export function peopleFromAssignments(
  seats: DraftRosterSeat[],
  assignments: DraftRosterAssignment[]
): PersonPick[] {
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]));
  return assignments.map((assignment) => ({
    user_guid: assignment.user_guid,
    name: assignment.name,
    email_address: assignment.email_address,
    position: nameById.get(assignment.positionId) ?? null,
    ...(assignment.isLeader ? { isLeader: true } : {}),
    ...(assignment.designation ? { designation: assignment.designation } : {}),
  }));
}

export function rosterNodesForCreate(
  seats: DraftRosterSeat[]
): RosterNodePayload[] {
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]));
  return seats.map((seat) => ({
    name: seat.name,
    parent: seat.parentId ? (nameById.get(seat.parentId) ?? null) : null,
    isWorkgroup: seat.isWorkgroup,
    category: seat.category,
    customCategory: seat.customCategory,
  }));
}

export function findIncidentCommanderSeat(
  seats: DraftRosterSeat[]
): DraftRosterSeat | undefined {
  return seats.find((seat) => isIncidentCommanderSeatName(seat.name));
}

export function assignmentsKeepingCreatorOnIc(
  seats: DraftRosterSeat[],
  assignments: DraftRosterAssignment[],
  creator: { user_guid: string; name: string; email_address: string } | null
): DraftRosterAssignment[] {
  if (!creator?.user_guid) return assignments;
  const ic = findIncidentCommanderSeat(seats);
  if (!ic) return assignments;
  const creatorOnIc = assignments.find(
    (assignment) =>
      assignment.positionId === ic.id &&
      assignment.user_guid === creator.user_guid
  );
  return [
    ...assignments.filter((assignment) => assignment.positionId !== ic.id),
    {
      ...creatorOnIc,
      positionId: ic.id,
      user_guid: creator.user_guid,
      name: creator.name,
      email_address: creator.email_address,
      isLeader: true,
    },
  ];
}

export function defaultDraftSeats(): DraftRosterSeat[] {
  return seatsFromTemplateNodes(DEFAULT_ICS_ROSTER_NODES);
}

const DEFAULT_ICS_SEAT_NAMES = new Set(
  DEFAULT_ICS_ROSTER_NODES.map((node) => node.name)
);

const EMPTY_ROSTER_CONTACT = {
  mobilePhone: null,
  homePhone: null,
  pager: null,
};

/**
 * Add template seats that are not already on the chart, wiring parents by name.
 * Existing seats and their assignees stay put.
 */
export function addMissingTemplateNodes(
  seats: DraftRosterSeat[],
  nodes: TemplateNode[]
): DraftRosterSeat[] {
  const existingNames = new Set(seats.map((seat) => seat.name));
  const used = new Set(seats.map((seat) => seat.id));
  const idByName = new Map(seats.map((seat) => [seat.name, seat.id]));
  const next = [...seats];

  for (const node of nodes) {
    if (existingNames.has(node.name)) continue;
    const id = nextSeatId(node.name, used);
    const parentId = node.parentName
      ? (idByName.get(node.parentName) ?? null)
      : null;
    idByName.set(node.name, id);
    existingNames.add(node.name);
    next.push({
      id,
      name: node.name,
      parentId,
      isWorkgroup: node.isWorkgroup,
      category: node.category,
      customCategory: node.customCategory,
    });
  }
  return next;
}

export function updateDraftSeat(
  seats: DraftRosterSeat[],
  seatId: string,
  patch: {
    name?: string;
    parentId?: string | null;
    isWorkgroup?: boolean;
  }
): DraftRosterSeat[] {
  const current = seats.find((seat) => seat.id === seatId);
  if (!current) return seats;

  const nextName = patch.name?.trim() || current.name;
  const nextParentId =
    patch.parentId !== undefined ? patch.parentId : current.parentId;
  const renamed = seats.map((seat) =>
    seat.id === seatId
      ? {
          ...seat,
          name: nextName,
          isWorkgroup: patch.isWorkgroup ?? seat.isWorkgroup,
        }
      : seat
  );
  if (nextParentId === current.parentId) return renamed;
  return reparentDraftSeat(renamed, seatId, nextParentId);
}

export function deleteDraftSeat(
  seats: DraftRosterSeat[],
  seatId: string
): DraftRosterSeat[] {
  const doomed = seats.find((seat) => seat.id === seatId);
  if (!doomed) return seats;
  return seats
    .filter((seat) => seat.id !== seatId)
    .map((seat) =>
      seat.parentId === seatId ? { ...seat, parentId: doomed.parentId } : seat
    );
}

export function addDraftAssignments(
  assignments: DraftRosterAssignment[],
  incoming: DraftRosterAssignment[]
): DraftRosterAssignment[] {
  let next = [...assignments];
  for (const row of incoming) {
    if (!row.user_guid || !row.positionId) continue;
    if (row.isLeader) {
      next = next.map((assignment) =>
        assignment.positionId === row.positionId
          ? { ...assignment, isLeader: false }
          : assignment
      );
    }
    if (row.designation) {
      next = clearDraftDesignation(next, row.positionId, row.designation);
    }
    const existingIndex = next.findIndex(
      (assignment) =>
        assignment.positionId === row.positionId &&
        assignment.user_guid === row.user_guid
    );
    if (existingIndex >= 0) {
      next[existingIndex] = { ...next[existingIndex], ...row };
      continue;
    }
    next.push(row);
  }
  return next;
}

/** A seat has one primary and one secondary; frees the slot for a new holder. */
function clearDraftDesignation(
  assignments: DraftRosterAssignment[],
  positionId: string,
  designation: DraftSeatDesignation
): DraftRosterAssignment[] {
  return assignments.map((assignment) =>
    assignment.positionId === positionId &&
    assignment.designation === designation
      ? {
          ...assignment,
          designation: null,
          ...(designation === 'primary' ? { isLeader: false } : {}),
        }
      : assignment
  );
}

/** Moves a seated person into a leader slot, or out of one with `null`. */
export function setDraftDesignation(
  assignments: DraftRosterAssignment[],
  userGuid: string,
  positionId: string,
  designation: DraftSeatDesignation | null
): DraftRosterAssignment[] {
  const cleared = designation
    ? clearDraftDesignation(assignments, positionId, designation)
    : assignments;
  return cleared.map((assignment) =>
    assignment.positionId === positionId && assignment.user_guid === userGuid
      ? {
          ...assignment,
          designation,
          isLeader:
            designation === 'primary'
              ? true
              : assignment.designation === 'primary'
                ? false
                : assignment.isLeader,
        }
      : assignment
  );
}

export function removeDraftAssignment(
  assignments: DraftRosterAssignment[],
  userGuid: string,
  positionId: string
): DraftRosterAssignment[] {
  return assignments.filter(
    (assignment) =>
      !(
        assignment.user_guid === userGuid &&
        assignment.positionId === positionId
      )
  );
}

export function removeDraftMember(
  assignments: DraftRosterAssignment[],
  userGuid: string
): DraftRosterAssignment[] {
  return assignments.filter((assignment) => assignment.user_guid !== userGuid);
}

export function dropAssignmentsForSeat(
  assignments: DraftRosterAssignment[],
  positionId: string
): DraftRosterAssignment[] {
  return assignments.filter(
    (assignment) => assignment.positionId !== positionId
  );
}

export function seatsToIncidentPositions(
  seats: DraftRosterSeat[]
): IncidentPosition[] {
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]));
  return seats.map((seat) => ({
    is_workgroup: !!seat.isWorkgroup,
    id: seat.id,
    item_id: seat.id,
    name: seat.name,
    parent_id: seat.parentId,
    parent_name: seat.parentId ? (nameById.get(seat.parentId) ?? null) : null,
    is_template: DEFAULT_ICS_SEAT_NAMES.has(seat.name),
    isResourcePosition: false,
    resourceSetId: null,
    resourceType: null,
    pointOfContactUserId: null,
    retire_next_op: false,
    category: seat.category ?? 'None',
    custom_category: seat.customCategory ?? null,
    layout_x: null,
    layout_y: null,
  }));
}

export function assignmentsToRosterEntries(
  seats: DraftRosterSeat[],
  assignments: DraftRosterAssignment[],
  groupId: string
): RosterEntry[] {
  const nameById = new Map(seats.map((seat) => [seat.id, seat.name]));
  const byUser = new Map<string, DraftRosterAssignment[]>();
  for (const assignment of assignments) {
    const rows = byUser.get(assignment.user_guid) ?? [];
    rows.push(assignment);
    byUser.set(assignment.user_guid, rows);
  }

  return [...byUser.entries()].map(([userId, rows]) => {
    const first = rows[0];
    return {
      incidentMemberId: userId,
      incidentId: '',
      groupId,
      userId,
      active: null,
      exited: null,
      incidentPositions: rows.map((row) => ({
        positionId: row.positionId,
        positionName: nameById.get(row.positionId) ?? '',
        isLeader: !!row.isLeader,
        designation: row.designation ?? null,
        positionAssignmentId: `${row.positionId}:${row.user_guid}`,
      })),
      activeTime: null,
      minutesWorked: null,
      activatedTimestamp: null,
      declinedTimestamp: null,
      activationNote: null,
      approvalAuthority: false,
      emailAddress: first.email_address || null,
      userName: first.name || null,
      homeAor: null,
      profileSettings: { userName: first.name },
      contact: EMPTY_ROSTER_CONTACT,
      relevantTeamMemberships: [],
    };
  });
}
